import { Store } from 'vuex';
import { Location } from '@/Stores/UserStore';
import { RootState, typedStore,  } from '@/store';
import vatsApi from '@/Shared/VatsAPI';
import { CitationSearchParams } from '@/Citation/CitationSearch/CitationSearchParams';
import { ICitationSearchResult } from '../Citation/CitationSearch/ICitationSearchResult';
import { Logger } from '@/Shared/Logger';
import i18n from '@/internationalization';
import { markModule } from './StoreTypes';
import { Dictionary } from 'vue-router/types/router';

export interface SearchPayload {
    parameters: CitationSearchParams;
    location: Location;
}

export interface CitationState {
    searchParams: Dictionary<CitationSearchParams>;
    searchResults: Dictionary<ICitationSearchResult[]>;
    searchError: string | null;
    searchInProgress: boolean;
}

export const citationModule = markModule({
    namespaced: true,
    state: {
        searchParams: {},
        searchResults: {},
        searchError: null,
        searchInProgress: false
    } as CitationState,
    mutations: {
        setSearchParams(state, payload: { params: CitationSearchParams, location: Location }) {
            const { params, location } = payload;
            state.searchParams = Object.assign({}, state.searchParams, {
                [location.urlSegment]: params
            });
            state.searchError = null;
        },
        setSearchResults(state, payload: { results: ICitationSearchResult[], location: Location }) {
            const { results, location } = payload;
            state.searchResults = Object.assign({}, state.searchResults, {
                [location.urlSegment]: results
            });
            state.searchError = null;
            state.searchInProgress = false;
        },
        clearSearch(state, location: Location) {
            state.searchParams[location.urlSegment] = new CitationSearchParams();
            state.searchResults[location.urlSegment] = [];
            state.searchError = null;
            state.searchInProgress = false;
        },
        setSearchError(state, error: string) {
            state.searchError = error;
            state.searchInProgress = false;
        },
        setSearchBegun(state) {
            state.searchInProgress = true;
        },
        setSearchEnd(state) {
            state.searchInProgress = false;
        }
    },
    getters: {
        searchParams(state, _, root) {
            if (root.currentLocation.locationId) {
                return state.searchParams[root.currentLocation.urlSegment] || new CitationSearchParams();
            }
            return new CitationSearchParams();
        },
        searchResults(state, _, root): ICitationSearchResult[] {
            if (root.currentLocation.locationId) {
                return state.searchResults[root.currentLocation.urlSegment] || [];
            }
            return [];
        },
        searchError: (state) => state.searchError,
        searchInProgress: (state) => state.searchInProgress
    },
    actions: {
        async beginSearch({ commit }, { parameters, location }: SearchPayload) {
            const nonDefaultParam = Object.keys(parameters)
                .find((key) => (parameters[key as keyof CitationSearchParams]) ? true : false);
            if (!nonDefaultParam) {
                return;
            }
            typedStore.modules.citationModule.mutations.setSearchParams({
                params: parameters,
                location
            });
            typedStore.modules.citationModule.mutations.setSearchBegun(null);
            try {
                const response = await vatsApi
                    .post<ICitationSearchResult[]>(`/${location.urlSegment}/citations/search`, parameters, {
                        headers: {
                            accept: 'application/json',
                        }
                    });
                typedStore.modules.citationModule.mutations.setSearchResults({
                    results: response.data,
                    location
                });
            }
            catch (e) {
                Logger.Log(e.message);
                typedStore.modules.citationModule.mutations
                    .setSearchError(e.message || i18n.t('citation.search.results.defaultSearchError'));
            }
        }
    },
    modules: {}
}, {} as RootState);
